// Phone numbers as people read them (Kevin 29/10) — display only: the data
// and every payload keep E.164 (« +33612345678 »).
// - French numbers (+33…, 0033…, or a 10-digit national 0X…) in the national
//   format, by pairs: « 06 12 34 56 78 » (as the backend's display_number).
// - Other numbers stay international, grouped: the country code (ITU E.164
//   assignment: 1 and 7 on one digit, a fixed list on two, the rest on
//   three), then +1 by 3-3-4, else by pairs (a leading group of three when
//   the count is odd): « +32 470 12 34 56 », « +44 20 79 46 09 58 ».
// Anything that is not a number (« anonymous », a masked caller, a short
// code) comes back as given.

const TWO_DIGIT_CODES = new Set([
  '20', '27', '30', '31', '32', '33', '34', '36', '39', '40', '41', '43', '44', '45', '46', '47', '48', '49',
  '51', '52', '53', '54', '55', '56', '57', '58', '60', '61', '62', '63', '64', '65', '66',
  '81', '82', '84', '86', '90', '91', '92', '93', '94', '95', '98',
]);

const pairs = (digits) => {
  const groups = [];
  let rest = digits;
  if (rest.length % 2 === 1 && rest.length > 3) {
    groups.push(rest.slice(0, 3));
    rest = rest.slice(3);
  }
  for (let i = 0; i < rest.length; i += 2) groups.push(rest.slice(i, i + 2));
  return groups.join(' ');
};

const countryCode = (digits) => {
  if (digits[0] === '1' || digits[0] === '7') return digits[0];
  const two = digits.slice(0, 2);
  return TWO_DIGIT_CODES.has(two) ? two : digits.slice(0, 3);
};

export const formatPhone = (value) => {
  if (value === null || value === undefined) return '';
  const raw = String(value).trim();
  if (!raw) return '';
  // Only digits, spaces, dots, dashes, parentheses and one leading « + ».
  if (!/^\+?[\d\s.\-()]+$/.test(raw)) return raw;
  let digits = raw.replace(/\D/g, '');
  let international = raw.startsWith('+');
  if (!international && digits.startsWith('00')) {
    digits = digits.slice(2);
    international = true;
  }
  if (international && /^33\d{9}$/.test(digits)) return pairs(`0${digits.slice(2)}`);
  if (!international) return /^0\d{9}$/.test(digits) ? pairs(digits) : raw;
  if (digits.length < 8 || digits.length > 15) return raw;
  const code = countryCode(digits);
  const national = digits.slice(code.length);
  if (code === '1' && national.length === 10) {
    return `+1 ${national.slice(0, 3)} ${national.slice(3, 6)} ${national.slice(6)}`;
  }
  return `+${code} ${pairs(national)}`;
};

// The label of a voice line { phone_number, display }: formatted here from
// the E.164 number, the backend's display as a fallback.
export const numberLabel = (number) => (number ? formatPhone(number.phone_number) || number.display || '' : '');

// Search: a typed « 06 12 » or « +336 12 » finds the stored +33612…
export const phoneMatches = (value, needle) => {
  const typed = String(needle || '').replace(/\D/g, '');
  if (!typed || typed.length < 2 || !value) return false;
  const stored = String(value).replace(/\D/g, '');
  const national = stored.startsWith('33') && stored.length === 11 ? `0${stored.slice(2)}` : stored;
  return stored.includes(typed) || national.includes(typed);
};
